import React, { useEffect, useMemo, useRef, useState } from 'react'

import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { format as formatDate } from 'date-fns'
import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'

import { CompleteButtonV2 } from './CompleteButtonV2'
import { TaskBaseItemV2 } from './TaseBaseItemV2'
import { setAppraisalOrderModalOpen, setTitleOrderDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch } from '@/client/hooks'
import TextEditorField from '@/components/FormikInputs/TextEditorField'
import { requestPreliminaryFloodReport } from '@/features/DealSummary/dropDownMenuItems'
import { TaskDotsMenu } from '@/features/SidePanel/TaskDotsMenu'
import { useUpdateChecklistItemMutation } from '@/services/application'
import { useGetLoggedUserQuery } from '@/services/user'
import { ChecklistItem } from '@/types/unlock'
import { isOpsManager } from '@/utils/user'

export type TaskItemProps = {
  task: ChecklistItem
  index: number
  draggableDisabled?: boolean
  toggleTask: (task: ChecklistItem) => void
  lastItem?: boolean
}

export const TaskItemV2 = (props: TaskItemProps) => {
  const router = useRouter()
  const { task } = props
  const dispatch = useAppDispatch()
  const { data: user } = useGetLoggedUserQuery()

  const [updateChecklistItem] = useUpdateChecklistItemMutation()

  const [editingNotes, setEditingNotes] = useState(false)
  const [showingDotsMenu, setShowingDotsMenu] = useState(false)

  const completed = useMemo(() => {
    return task.status === 'done'
  }, [task.status])

  const focusRef = useRef(null)

  // Set focus to "notes" input box
  useEffect(() => {
    focusRef.current && focusRef.current.focus()
  }, [editingNotes])

  const NotesSection = useMemo(() => {
    const updateTask = (notes: string) => {
      updateChecklistItem({
        ...task,
        notes,
      })
      // Timeout to avoid reopening when clicking on task name
      setTimeout(() => setEditingNotes(false), 100)
    }

    if (editingNotes) {
      return (
        <div className='mt-4'>
          <Formik
            initialValues={{ notes: task.notes }}
            onSubmit={(values) => {
              updateTask(values.notes)
            }}
          >
            {({ isSubmitting, setFieldValue, submitForm }) => (
              <Form>
                <TextEditorField
                  ref={focusRef}
                  id='notes'
                  placeholder='Enter Notes'
                  name='notes'
                  disabled={isSubmitting}
                  onChangeApplied={submitForm}
                  setFieldValue={setFieldValue}
                />
              </Form>
            )}
          </Formik>
        </div>
      )
    } else if (task.notes) {
      return (
        <p
          dangerouslySetInnerHTML={{ __html: task.notes }}
          className='text-[12px] italic max-h-[36px] overflow-hidden whitespace-pre-wrap text-ellipsis'
          style={{
            // Hack to show ellipsis and two lines of text (https://stackoverflow.com/a/54597262/6456960)
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: '2',
          }}
        />
      )
    } else {
      return null
    }
  }, [editingNotes, task, updateChecklistItem])

  // Only way of checking without adding column to db
  const extraSlot = useMemo(() => {
    if (
      task?.name?.toLowerCase()?.includes('property reports') &&
      task?.name?.toLowerCase()?.includes('title work')
    )
      return (
        <div className='flex mt-3 justify-center'>
          <button
            className='btn btn-primary btn-sm'
            onClick={() => {
              dispatch(setTitleOrderDrawerOpen(true))
            }}
          >
            Make an Order
          </button>
        </div>
      )

    if (
      task.name.includes('Order Appraisal') ||
      task.name.includes('Appraisal Ordered') ||
      task.name.includes('Property Reports - Ordered Appraisal') // Backwards compatibility
    )
      return (
        <div className='flex mt-3 justify-center'>
          <button
            className='btn btn-primary btn-sm'
            onClick={() => dispatch(setAppraisalOrderModalOpen(true))}
          >
            Make an Order
          </button>
        </div>
      )

    if (task.name === 'Review Appraisal Report') {
      return (
        <div className='flex mt-3 justify-center'>
          <button
            className='btn btn-primary btn-sm'
            onClick={() => router.push(`/applications/${router.query.id}/underwriting`)}
          >
            Open Underwriting Section
          </button>
        </div>
      )
    }

    if (task.name.includes('Order Flood Determination'))
      return (
        <div className='flex mt-3 justify-center'>
          <button
            className='btn btn-primary btn-sm'
            onClick={() => requestPreliminaryFloodReport(`${router.query.id}`)}
          >
            Make an Order
          </button>
        </div>
      )

    return null
  }, [dispatch, task.name, router])

  return (
    <TaskBaseItemV2
      {...props}
      extraSlot={extraSlot}
    >
      <div className='mt-[3px]'>
        <CompleteButtonV2
          taskId={task.id}
          completed={completed}
          onToggleComplete={() => props.toggleTask(task)}
        />
      </div>
      <div
        className='grow w-32'
        onClick={() => {
          !editingNotes && setEditingNotes(true)
        }}
      >
        <p className={`text-[13px] ${completed ? 'line-through' : ''}`}>{task.name}</p>
        {NotesSection}
        <>
          {isOpsManager(user) && task.is_audit_pass !== null && (
            <div className='mt-4 flex flex-row'>
              <div className='mr-1'>
                {task?.is_audit_pass ? (
                  <CheckIcon
                    fill='green'
                    width={15}
                  />
                ) : (
                  <XMarkIcon
                    fill='red'
                    width={15}
                  />
                )}
              </div>
              <div className='text-xs font-bold'>
                <p>
                  {task.is_audit_pass ? `Pass` : `Fail`} -{' '}
                  {task?.audit_manager?.data?.full_name
                    ? task?.audit_manager?.data?.full_name
                    : task?.audit_manager?.username
                      ? task?.audit_manager?.username
                      : task?.audit_manager?.email}{' '}
                  {/* Adding T12 to the date to ignore the timezone */}
                  {formatDate(new Date(`${task?.audit_date}T12:00:00`), 'MM/dd/yy')}
                </p>
              </div>
            </div>
          )}
        </>
      </div>
      <div
        className='relative'
        onMouseEnter={() => setShowingDotsMenu(true)}
        onMouseLeave={() => setShowingDotsMenu(false)}
      >
        <EllipsisVerticalIcon
          width={20}
          className='shrink-0 cursor-pointer'
        />
        {showingDotsMenu && (
          <div className='absolute right-[20px] top-[-30px]'>
            <TaskDotsMenu
              task={task}
              setEditingNotes={setEditingNotes}
              setShowingDotsMenu={setShowingDotsMenu}
              toggleTask={props.toggleTask}
            />
          </div>
        )}
      </div>
    </TaskBaseItemV2>
  )
}
